import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ZoomIn, ZoomOut, RotateCw } from "lucide-react";

interface ManualReviewEditorProps {
  bill: any;
  isOpen: boolean;
  onClose: () => void;
  onAccept?: (updatedData: any) => Promise<void>;
}

export function ManualReviewEditor({ bill, isOpen, onClose, onAccept }: ManualReviewEditorProps) {
  const [parsedData, setParsedData] = useState(bill?.extracted_data || {});
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [saving, setSaving] = useState(false);

  const updateField = (field: string, value: any) => {
    setParsedData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleAccept = async () => {
    if (!onAccept) return;
    setSaving(true);
    try {
      await onAccept(parsedData);
      onClose();
    } catch (error) {
      console.error('Accept failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceScore = (field: string) => {
    return bill?.confidence_scores?.[field] || bill?.ai_confidence || 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-screen-2xl h-[90vh] p-0">
        <div className="flex h-full">
          
          {/* LEFT: PDF Viewer (50%) */}
          <div className="w-1/2 border-r bg-muted/20 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-background">
              <h3 className="font-semibold">Bill Preview</h3>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setZoom(Math.max(50, zoom - 10))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">{zoom}%</span>
                <Button size="sm" variant="outline" onClick={() => setZoom(Math.min(200, zoom + 10))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRotation((r) => (r + 90) % 360)}>
                  <RotateCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
              {bill?.file_path || bill?.file_url ? (
                <iframe
                  src={bill.file_url || bill.file_path}
                  className="w-full h-full border rounded bg-background"
                  style={{ 
                    transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    transformOrigin: 'center'
                  }}
                />
              ) : (
                <div className="text-center text-muted-foreground">
                  <p>No preview available</p>
                  <p className="text-sm mt-2">{bill?.file_name || 'Unknown file'}</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Parsed Fields Form (50%) */}
          <div className="w-1/2 flex flex-col">
            <div className="p-4 border-b bg-background">
              <h3 className="font-semibold">Parsed Fields</h3>
              <p className="text-sm text-muted-foreground">
                Review and edit extracted information
              </p>
            </div>

            <ScrollArea className="flex-1 p-6">
              <div className="space-y-6">
                
                {/* Account Number */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="account_number">Account Number *</Label>
                    <ConfidenceBadge score={getConfidenceScore('account_number')} />
                  </div>
                  <Input
                    id="account_number"
                    value={parsedData.account_number || ''}
                    onChange={(e) => updateField('account_number', e.target.value)}
                    placeholder="Enter account number"
                  />
                </div>

                {/* Amount Due */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="amount_due">Amount Due *</Label>
                    <ConfidenceBadge score={getConfidenceScore('amount_due')} />
                  </div>
                  <Input
                    id="amount_due"
                    type="number"
                    step="0.01"
                    value={parsedData.amount_due || parsedData.amount || ''}
                    onChange={(e) => updateField('amount_due', parseFloat(e.target.value))}
                    placeholder="0.00"
                  />
                </div>

                {/* Bill Date */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bill_date">Bill Date *</Label>
                    <ConfidenceBadge score={getConfidenceScore('bill_date')} />
                  </div>
                  <Input
                    id="bill_date"
                    type="date"
                    value={parsedData.bill_date || ''}
                    onChange={(e) => updateField('bill_date', e.target.value)}
                  />
                </div>

                {/* Due Date */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="due_date">Due Date *</Label>
                    <ConfidenceBadge score={getConfidenceScore('due_date')} />
                  </div>
                  <Input
                    id="due_date"
                    type="date"
                    value={parsedData.due_date || ''}
                    onChange={(e) => updateField('due_date', e.target.value)}
                  />
                </div>

                {/* Supplier Name */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="supplier_name">Supplier/Utility</Label>
                    <ConfidenceBadge score={getConfidenceScore('supplier_name')} />
                  </div>
                  <Input
                    id="supplier_name"
                    value={parsedData.supplier_name || ''}
                    onChange={(e) => updateField('supplier_name', e.target.value)}
                    placeholder="Utility company name"
                  />
                </div>

                {/* Meter Number */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="meter_number">Meter Number</Label>
                    <ConfidenceBadge score={getConfidenceScore('meter_number')} />
                  </div>
                  <Input
                    id="meter_number"
                    value={parsedData.meter_number || ''}
                    onChange={(e) => updateField('meter_number', e.target.value)}
                    placeholder="Optional"
                  />
                </div>

                {/* Period Start */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="period_start">Period Start</Label>
                    <ConfidenceBadge score={getConfidenceScore('period_start')} />
                  </div>
                  <Input
                    id="period_start"
                    type="date"
                    value={parsedData.period_start || parsedData.billing_period_start || ''}
                    onChange={(e) => updateField('period_start', e.target.value)}
                  />
                </div>

                {/* Period End */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="period_end">Period End</Label>
                    <ConfidenceBadge score={getConfidenceScore('period_end')} />
                  </div>
                  <Input
                    id="period_end"
                    type="date"
                    value={parsedData.period_end || parsedData.billing_period_end || ''}
                    onChange={(e) => updateField('period_end', e.target.value)}
                  />
                </div>

                {/* Consumption */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="consumption">Consumption (Usage)</Label>
                    <ConfidenceBadge score={getConfidenceScore('consumption')} />
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="consumption"
                      type="number"
                      value={parsedData.consumption?.usage || parsedData.usage_amount || ''}
                      onChange={(e) => updateField('consumption', { 
                        ...parsedData.consumption, 
                        usage: parseFloat(e.target.value) 
                      })}
                      placeholder="Usage amount"
                      className="flex-1"
                    />
                    <Input
                      value={parsedData.consumption?.unit || parsedData.usage_unit || 'kWh'}
                      onChange={(e) => updateField('consumption', { 
                        ...parsedData.consumption, 
                        unit: e.target.value 
                      })}
                      placeholder="Unit"
                      className="w-24"
                    />
                  </div>
                </div>

              </div>
            </ScrollArea>

            {/* Action Buttons */}
            <div className="p-6 border-t bg-background">
              <div className="flex gap-2">
                <Button 
                  onClick={handleAccept} 
                  className="flex-1"
                  size="lg"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Accept'}
                </Button>
                <Button variant="outline" size="lg">
                  Re-parse
                </Button>
                <Button variant="outline" size="lg">
                  Link Account
                </Button>
                <Button variant="outline" size="lg">
                  Create Account
                </Button>
                <Button 
                  variant="destructive" 
                  size="lg"
                  onClick={onClose}
                >
                  Reject
                </Button>
              </div>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfidenceBadge({ score }: { score: number }) {
  const percentage = Math.round(score * 100);
  
  if (score >= 0.95) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400">
        🟢 {percentage}%
      </Badge>
    );
  } else if (score >= 0.85) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400">
        🟡 {percentage}%
      </Badge>
    );
  } else {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400">
        🔴 {percentage}%
      </Badge>
    );
  }
}
